import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { MessageCircle, X, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { stepFromPath } from '../stepFromPath';
import { sendChatMessage, type ChatMessage } from '../services/chatbotService';
import { useChatPageContext } from '../ChatContext';

/**
 * Floating in-app assistant. Shown only on tool routes (where stepFromPath returns
 * a step). Sends the recent conversation + the current step + language to the
 * backend and renders Claude's reply.
 */
export default function ChatWidget() {
  const { t, i18n } = useTranslation('chatbot');
  const location = useLocation();
  const step = stepFromPath(location.pathname);

  const pageContext = useChatPageContext();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && typeof endRef.current?.scrollIntoView === 'function') {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  if (!step) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setError(null);
    setSending(true);
    try {
      const reply = await sendChatMessage(next, step, i18n.language === 'de' ? 'de' : 'en', pageContext ?? undefined);
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch {
      setError(t('error'));
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('launch')}
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-opacity hover:opacity-90"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label={t('title')}
      className="fixed bottom-5 right-5 z-40 flex h-[32rem] max-h-[80vh] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="font-semibold text-foreground">{t('title')}</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('close')}
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        <p className="text-sm text-muted-foreground">{t('greeting')}</p>
        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div
              key={i}
              className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-white"
            >
              {m.content}
            </div>
          ) : (
            <div
              key={i}
              className="mr-auto w-fit max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm text-foreground [&_a]:underline [&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:py-0.5 [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:my-1 [&_strong]:font-semibold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4"
            >
              <ReactMarkdown>{m.content}</ReactMarkdown>
            </div>
          ),
        )}
        {sending && <p className="text-sm text-muted-foreground">…</p>}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="flex items-center gap-2 border-t border-border px-3 py-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={1500}
          placeholder={t('placeholder')}
          aria-label={t('placeholder')}
          className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          aria-label={t('send')}
          className="flex h-9 items-center gap-1 rounded-md bg-primary px-3 text-sm text-white disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          <span className="hidden sm:inline">{t('send')}</span>
        </button>
      </form>
    </div>
  );
}
